/**
 * Les règles d'un centre, sans réseau : ce qu'une saisie doit contenir, ce
 * qu'on enregistre, ce que veut dire un refus de la base, l'ordre des classes.
 *
 * `gateway.supabase.ts` lit et écrit ; la base tranche (code lisible et
 * unique, un stage seulement dans un centre de vacances, un centre de vacances
 * gardé sous ses stages à venir, la suppression d'une classe — migration
 * `20261020090000_les_centres`). Ce module annonce ses refus avant le clic, et
 * traduit ceux qu'elle oppose quand même.
 */
import type { ClasseDuCentre } from '../jpa/regles';

export type StatutDeCentre = 'actif' | 'pause' | 'test';

export interface Centre {
  id: string;
  nom: string;
  code: string;
  adresse: string;
  adresse2: string | null;
  codePostal: string;
  localite: string;
  telephone: string | null;
  statut: StatutDeCentre;
  archive: boolean;
  archiveLe: string | null;
  /** `null` : la base ne porte pas encore la colonne (journées pédagogiques non installées). */
  centreDeVacances: boolean | null;
  ecoleId: string | null;
}

export interface SaisieDeCentre {
  nom: string;
  code: string;
  adresse: string;
  adresse2: string;
  codePostal: string;
  localite: string;
  telephone: string;
  statut: StatutDeCentre;
  centreDeVacances: boolean;
  /** `''` : aucune école. */
  ecoleId: string;
}

/*
  Un centre nouveau est un centre de vacances actif : c'est ce que tous les
  centres étaient jusqu'ici. Un lieu de journées pédagogiques seulement se
  choisit, il ne se suppose pas.
*/
export const SAISIE_VIDE: SaisieDeCentre = {
  nom: '', code: '', adresse: '', adresse2: '', codePostal: '', localite: '', telephone: '',
  statut: 'actif', centreDeVacances: true, ecoleId: '',
};

export function saisieDuCentre(c: Centre): SaisieDeCentre {
  return {
    nom: c.nom,
    code: c.code,
    adresse: c.adresse,
    adresse2: c.adresse2 ?? '',
    codePostal: c.codePostal,
    localite: c.localite,
    telephone: c.telephone ?? '',
    statut: c.statut,
    centreDeVacances: c.centreDeVacances ?? true,
    ecoleId: c.ecoleId ?? '',
  };
}

/** Le code entre dans les numéros de facture : `CDV-<code>-…`, `JPA-<code>-…`. */
export const FORME_DU_CODE = /^[A-Z0-9]{2,10}$/;

/** Le code tel qu'on l'enregistre : sans espaces autour, en majuscules. */
export function codeSaisi(texte: string): string {
  return texte.trim().toUpperCase();
}

export type ChampDuCentre = 'nom' | 'code' | 'adresse' | 'codePostal' | 'localite';

/** Ce qui manque ou ne va pas, champ par champ. Vide : la saisie peut partir. */
export function erreursDeSaisie(
  s: SaisieDeCentre,
  codesDesAutresCentres: string[],
): Partial<Record<ChampDuCentre, string>> {
  const erreurs: Partial<Record<ChampDuCentre, string>> = {};
  const code = codeSaisi(s.code);
  if (!s.nom.trim()) erreurs.nom = 'Le nom du centre est obligatoire.';
  if (!code) erreurs.code = 'Le code est obligatoire.';
  else if (!FORME_DU_CODE.test(code)) erreurs.code = 'Le code : 2 à 10 lettres majuscules ou chiffres, sans espace ni tiret.';
  else if (codesDesAutresCentres.some((c) => codeSaisi(c) === code)) erreurs.code = 'Ce code est déjà celui d’un autre centre.';
  if (!s.adresse.trim()) erreurs.adresse = 'L’adresse est obligatoire.';
  if (!/^\d{4}$/.test(s.codePostal.trim())) erreurs.codePostal = 'Le code postal : 4 chiffres.';
  if (!s.localite.trim()) erreurs.localite = 'La localité est obligatoire.';
  return erreurs;
}

/**
 * La ligne de `centers` à écrire. Sans les colonnes des journées
 * pédagogiques tant que la base ne les porte pas : un écran déployé avant la
 * migration enregistre encore un centre.
 */
export function ligneDuCentre(s: SaisieDeCentre, avecColonnesJpa: boolean): Record<string, unknown> {
  const vide = (t: string) => (t.trim() ? t.trim() : null);
  return {
    name: s.nom.trim(),
    tag: codeSaisi(s.code),
    address: s.adresse.trim(),
    address2: vide(s.adresse2),
    postal_code: s.codePostal.trim(),
    city: s.localite.trim(),
    phone: vide(s.telephone),
    statut: s.statut,
    ...(avecColonnesJpa ? { centre_de_vacances: s.centreDeVacances, school_id: s.ecoleId || null } : {}),
  };
}

/** Ce qu'un refus de la base veut dire, en français, sans étiquette technique. */
export function messageDeLaBase(e: unknown): string {
  const o = (e ?? {}) as { code?: string; message?: string; details?: string };
  const texte = `${o.message ?? ''} ${o.details ?? ''}`;
  if (o.code === '23505' && texte.includes('centers_code_unique')) return 'Ce code est déjà celui d’un autre centre.';
  if (o.code === '23514' && texte.includes('centers_code_lisible')) {
    return 'Le code : 2 à 10 lettres majuscules ou chiffres, sans espace ni tiret.';
  }
  if (o.code === '23505' && texte.includes('centre_classes_libelle_unique')) return 'Une classe porte déjà ce nom dans ce centre.';
  if (o.code === '23503' && texte.includes('sessions')) {
    return 'Ce centre a des sessions : il ne se supprime pas, il s’archive.';
  }
  if (o.code === '23503' && texte.includes('kid_classes')) {
    return 'Des élèves sont rangés dans ses classes : supprimez d’abord ses classes, ou archivez le centre.';
  }
  const etiquette = (o.message ?? '').match(/^\[(stage-hors-centre-de-vacances|centre-de-vacances-utilise)\]\s*(.*)$/);
  if (etiquette) return etiquette[2].charAt(0).toUpperCase() + etiquette[2].slice(1);
  if (o.message) return o.message;
  return e instanceof Error ? e.message : String(e);
}

/**
 * Monter ou descendre une classe : la liste renumérotée de 1 à n après
 * l'échange, et seulement les classes dont l'ordre change. Renuméroter
 * plutôt qu'échanger deux valeurs : deux classes au même ordre (ajoutées à
 * la main) s'échangeraient sans bouger.
 */
export function ordreApresDeplacement(
  classes: ClasseDuCentre[],
  index: number,
  sens: -1 | 1,
): { id: string; ordre: number }[] {
  const cible = index + sens;
  if (index < 0 || index >= classes.length || cible < 0 || cible >= classes.length) return [];
  const liste = [...classes];
  [liste[index], liste[cible]] = [liste[cible], liste[index]];
  return liste
    .map((c, i) => ({ id: c.id, ordre: i + 1, avant: c.ordre }))
    .filter((c) => c.ordre !== c.avant)
    .map(({ id, ordre }) => ({ id, ordre }));
}

/** Ce que la suppression d'une classe va faire, dit avant le clic. */
export function consequencesDeLaSuppression(
  eleves: number,
  journees: number,
  remplacante: string | null,
): string[] {
  const phrases: string[] = [];
  if (eleves > 0) {
    phrases.push(remplacante
      ? `${eleves} élève${eleves > 1 ? 's' : ''} ${eleves > 1 ? 'passent' : 'passe'} dans « ${remplacante} ».`
      : `${eleves} élève${eleves > 1 ? 's' : ''} n’${eleves > 1 ? 'ont' : 'a'} plus de classe : ${eleves > 1 ? 'leurs' : 'ses'} parents la choisiront à la prochaine inscription.`);
  }
  if (journees > 0) {
    phrases.push(remplacante
      ? `${journees} journée${journees > 1 ? 's' : ''} qui l’admettai${journees > 1 ? 'ent' : 't'} admet${journees > 1 ? 'tent' : ''} « ${remplacante} » à sa place.`
      : `${journees} journée${journees > 1 ? 's' : ''} ne l’admet${journees > 1 ? 'tent' : ''} plus.`);
  }
  phrases.push('Aucune inscription ni facture n’est touchée.');
  return phrases;
}

/**
 * Les centres de chaque école, par identifiant d'école : le lien
 * `centers.school_id` vu depuis la page des écoles. Les centres en activité
 * d'abord, les archivés ensuite.
 */
export function centresParEcole(centres: Centre[]): Map<string, Centre[]> {
  const parEcole = new Map<string, Centre[]>();
  const ranges = [...centres].sort((a, b) => Number(a.archive) - Number(b.archive) || a.nom.localeCompare(b.nom, 'fr'));
  for (const c of ranges) {
    if (c.ecoleId) parEcole.set(c.ecoleId, [...(parEcole.get(c.ecoleId) ?? []), c]);
  }
  return parEcole;
}

/** Ce que la suppression d'une école retire à ses centres, dit avant le clic ; `null` si rien. */
export function avantDeSupprimerUneEcole(centres: Centre[]): string | null {
  const enActivite = centres.filter((c) => !c.archive);
  if (enActivite.length === 0) return null;
  const noms = enActivite.map((c) => `« ${c.nom} »`).join(', ');
  return enActivite.length > 1
    ? `Les centres ${noms} n’auront plus d’école : leurs journées pédagogiques ne seront plus ouvertes aux parents.`
    : `Le centre ${noms} n’aura plus d’école : ses journées pédagogiques ne seront plus ouvertes aux parents.`;
}

export const NOM_DU_STATUT: Record<StatutDeCentre, string> = {
  actif: 'Actif',
  pause: 'En pause',
  test: 'Test',
};

export const DESCRIPTION_DU_STATUT: Record<StatutDeCentre, string> = {
  actif: 'Ses stages paraissent sur le site public et acceptent les inscriptions.',
  pause: 'Rien de ce centre n’est publié. Il reste disponible ici pour programmer ou consulter.',
  test: 'Centre d’essai : ses stages ne paraissent jamais sur le site, et il n’écrit à personne.',
};
